/**
 * Quelle marque sert cette page, et quels ecrans lui appartiennent.
 *
 * POURQUOI CE FICHIER EXISTE
 *
 * Le produit porte deux entrees commerciales sur le meme code. CFTSign est le parcours
 * terrain -- l'installateur, le poseur, l'activation chez le client. Flexcore est
 * l'application de gestion. Jusqu'ici les deux vivaient sur une seule adresse, et
 * l'ecran de connexion affichait le logo de l'un avec le nom de l'autre.
 *
 * Desormais chaque marque a son domaine, et un poseur qui arrive sur CFTSign ne doit
 * jamais tomber sur le CRM, meme en tapant l'adresse a la main.
 *
 * CE QUE CETTE SEPARATION N'EST PAS
 *
 * Ce n'est pas une frontiere de securite. Les deux domaines servent le MEME serveur :
 * un administrateur connecte depuis CFTSign garde le droit d'appeler l'API de gestion.
 * La separation decide de ce qu'on VOIT, jamais de ce qu'on PEUT. Le cloisonnement
 * reel, lui, vit dans les gardes de role, cote serveur.
 */

// Reconnu sur le nom d'hote, jamais sur une variable de build : la meme image sert les
// quatre adresses. Un build par marque doublerait le deploiement pour rien.
const HOTES_CFTSIGN = ['cftsign.fr', 'www.cftsign.fr', 'staging.cftsign.fr'];

export const CFTSIGN = 'cftsign';
export const FLEXCORE = 'flexcore';

export function marqueDuDomaine(hote) {
  const h = (hote || (typeof window !== 'undefined' ? window.location.hostname : '') || '')
    .toLowerCase();
  return HOTES_CFTSIGN.includes(h) ? CFTSIGN : FLEXCORE;
}

export const MARQUE = marqueDuDomaine();
export const estCftsign = MARQUE === CFTSIGN;

/**
 * Ce que chaque marque met devant les yeux : son nom, son logo, sa couleur.
 *
 * LA COULEUR EST ICI, ET PAS RECOPIEE DANS QUARANTE COMPOSANTS. C'est ce qui permet de
 * la changer sans rouvrir un seul ecran -- elle a change deux fois en dix jours, et
 * aucun des douze fichiers du parcours n'a ete retouche la seconde fois.
 *
 * ⚠️ LES DEUX MARQUES PORTENT LE MEME BLEU NUIT DEPUIS LE 07/09/2026, ET C'EST VOULU.
 *
 * Le 28/08, CFTSign etait passe au vert-gris a la demande de David : il se perdait
 * entre les deux applications pendant ses essais -- « des fois je me gourais ou
 * j'etais ». Le 07/09, Boubacar a tranche l'inverse, sans ambiguite : CFTSign revient
 * au bleu nuit de FlexCore, a l'identique.
 *
 * CE QUE CE CHOIX COUTE, ET IL FAUT LE SAVOIR AVANT DE LE DEFAIRE. Les deux entrees
 * deviennent VISUELLEMENT indistinguables. Le domaine continue de decider le nom, le
 * logo, l'icone Apple et le manifeste ; il ne decide plus la teinte. La confusion que
 * le vert existait pour lever redevient donc possible, et ce sont desormais le nom
 * dans l'onglet et le logo a l'ecran de connexion qui la levent, seuls.
 *
 * ⚠️ CE N'EST PAS UNE REGRESSION, NE LA « REPARE » PAS. Six valeurs identiques deux a
 * deux ressemblent a un copier-coller rate ; c'en est l'exact contraire. Le mecanisme
 * du lot S est intact -- la marque se decide toujours au nom d'hote, les six variables
 * sont toujours posees a l'execution. Seule la DONNEE a change. Remettre du vert ici
 * defait une decision datee, ce n'est pas corriger un defaut.
 *
 * EFFET DE BORD HEUREUX. `cftsign-192.png` est dessine sur le bleu nuit : le logo de
 * CFTSign portait donc jusqu'ici la couleur de l'AUTRE marque. La dette avait ete
 * relevee au lot marque et jamais payee, faute de dessinateur. Elle se ferme d'elle-meme
 * aujourd'hui, sans qu'une seule image soit retouchee.
 */
export const IDENTITES = {
  [CFTSIGN]: {
    nom: 'CFTSign',
    logo: '/cftsign-192.png',
    iconeApple: '/cftsign-apple.png',
    manifeste: '/manifest-cftsign.json',
    // Le meme bleu nuit que FlexCore, au chiffre pres. Voir la note ci-dessus : ces
    // trois valeurs sont volontairement identiques a celles de l'autre marque.
    couleur: '#1a1a2e',
    couleurClaire: '#2d2d5f',
    couleurBordure: '#2a2a3e',
  },
  [FLEXCORE]: {
    nom: 'FlexCore',
    logo: '/icon-192.png',
    iconeApple: '/apple-touch-icon.png',
    manifeste: '/manifest-flexcore.json',
    couleur: '#1a1a2e',
    couleurClaire: '#2d2d5f',
    couleurBordure: '#2a2a3e',
  },
};

/**
 * IL N'Y A PAS DE CHAMP `favicon`, ET C'EST DELIBERE. L'icone de l'onglet est le `logo`,
 * qui est deja le carre de 192 pixels de chaque marque. Ecrire les deux cote a cote
 * fabriquerait deux sources pour une seule image : elles divergeraient au premier
 * changement de logo, et la divergence serait invisible -- le bon logo a l'ecran de
 * connexion, l'ancien dans l'onglet. Meme raison que la couleur, calculee plus bas
 * plutot que recopiee.
 *
 * L'ICONE APPLE, ELLE, EST UN FICHIER A PART : 180 pixels, avec le fond opaque et les
 * marges qu'iOS exige. Les deux existent deja dans `public/`, ce sont des images
 * differentes, elles se declarent donc separement.
 */

export const IDENTITE = IDENTITES[MARQUE];

/**
 * Le hexadecimal en canaux rouge/vert/bleu, la forme qu'attend le theme Tailwind.
 *
 * POURQUOI CETTE CONVERSION EXISTE. Tailwind ne sait rendre un fond de marque a 50 %
 * que si la couleur lui est donnee en canaux : `rgb(var(--x) / <alpha-value>)`. La
 * forme brute `var(--marque-accent) / .5` n'est pas une declaration CSS valide -- le
 * navigateur la jette en silence et la classe ne peint rien.
 *
 * ⚠️ LE NOM DE LA CLASSE N'EST PAS ECRIT ICI, ET C'EST VOLONTAIRE. Le balayage de
 * Tailwind est TEXTUEL : il ne saute pas les commentaires. Citer une classe dans une
 * note d'un fichier de `src/` suffit a la faire compiler dans la feuille livree.
 * Mesure le 31/08 : une regle de fond de marque a demi-opacite, que personne n'emploie,
 * s'etait ajoutee au paquet livre -- uniquement parce qu'une note la nommait. Cette
 * note-ci se garde donc d'ecrire le moindre nom de classe.
 *
 * POURQUOI ON CALCULE AU LIEU DE RECOPIER. Ecrire « 26 26 46 » a cote de `#1a1a2e`
 * fabriquerait deux sources pour une meme couleur. Elles divergeraient au premier
 * changement de teinte, et le defaut serait invisible : la bonne couleur dans l'onglet,
 * la mauvaise a l'ecran.
 */
function enCanaux(hexadecimal) {
  const valeur = parseInt(hexadecimal.replace('#', ''), 16);
  return [(valeur >> 16) & 255, (valeur >> 8) & 255, valeur & 255].join(' ');
}

/**
 * La meme couleur en canaux teinte/saturation/luminosite, pour les jetons shadcn.
 *
 * POURQUOI DEUX FORMES. Le theme de ce projet porte deux familles de couleurs, arrivees
 * a deux moments. Les jetons shadcn (`--primary`, `--background`, `--border`...) sont
 * declares en TSL et consommes en `hsl(var(--x))`. Les couleurs de marque, posees par le
 * lot S, sont en RVB et consommees en `rgb(var(--x))`. Convertir est moins couteux que
 * de reecrire l une des deux familles, et surtout moins risque : `--background` et ses
 * quinze voisins servent tous les ecrans des deux applications.
 */
function enCanauxTsl(hexadecimal) {
  const valeur = parseInt(hexadecimal.replace('#', ''), 16);
  const r = ((valeur >> 16) & 255) / 255;
  const v = ((valeur >> 8) & 255) / 255;
  const b = (valeur & 255) / 255;
  const haut = Math.max(r, v, b);
  const bas = Math.min(r, v, b);
  const luminosite = (haut + bas) / 2;
  const ecart = haut - bas;

  let teinte = 0;
  let saturation = 0;
  if (ecart !== 0) {
    saturation = ecart / (1 - Math.abs(2 * luminosite - 1));
    if (haut === r) teinte = ((v - b) / ecart) % 6;
    else if (haut === v) teinte = (b - r) / ecart + 2;
    else teinte = (r - v) / ecart + 4;
    teinte *= 60;
    if (teinte < 0) teinte += 360;
  }
  return `${Math.round(teinte)} ${Math.round(saturation * 100)}% `
    + `${Math.round(luminosite * 100)}%`;
}

/**
 * Repose le titre de l'onglet, la couleur de la barre du navigateur et les variables
 * de couleur, d'apres la marque qui sert la page.
 *
 * POURQUOI A L'EXECUTION ET PAS AU BUILD. `public/index.html` est servi tel quel aux
 * QUATRE adresses : une seule image, quatre domaines. Tout ce qui y est ecrit en dur
 * est donc faux pour trois d'entre elles. Un installateur sur cftsign.fr lisait
 * « FlexCore » dans son onglet, au moment precis ou il fait signer un client, et le
 * nom d'un editeur qu'il ne connait pas n'inspire rien de bon a ce moment-la.
 *
 * Appelee avant le rendu, dans `index.js` : le titre doit etre juste des le premier
 * affichage, pas apres un battement.
 */
/**
 * Repose un `<link>` de l'en-tete, ou le cree s'il n'y en a pas.
 *
 * Cree, et pas seulement repose : `public/index.html` ne porte AUCUN `<link rel="icon">`
 * -- mesure du 02/09, la seule occurrence du mot « favicon » y etait le commentaire
 * livre par Create React App. Un poseur qui se contenterait de reecrire l'existant ne
 * ferait donc rien du tout, et la suite resterait verte pendant que l'onglet reste vide.
 */
function poserLien(rel, href, attributs = {}) {
  if (!href) return null;
  // `rel~=` compare des MOTS separes par des espaces : il attrape « shortcut icon »
  // sans attraper « apple-touch-icon », qui est un seul mot et un autre role.
  const selecteur = rel === 'icon' ? 'link[rel~="icon"]' : `link[rel="${rel}"]`;
  let lien = document.querySelector(selecteur);
  if (!lien) {
    lien = document.createElement('link');
    lien.setAttribute('rel', rel);
    document.head.appendChild(lien);
  }
  lien.setAttribute('href', href);
  Object.entries(attributs).forEach(([nom, valeur]) => lien.setAttribute(nom, valeur));
  return lien;
}

export function appliquerMarque(identite = IDENTITE) {
  if (typeof document === 'undefined') return;

  document.title = identite.nom;

  const barre = document.querySelector('meta[name="theme-color"]');
  if (barre) barre.setAttribute('content', identite.couleur);

  // L'ONGLET, L'ECRAN D'ACCUEIL ET LE MANIFESTE, POSES ICI POUR LA MEME RAISON QUE LE
  // TITRE. Les trois etaient ecrits en dur dans `public/index.html` et dans
  // `public/manifest.json`, servis tels quels aux QUATRE adresses : l'icone Apple de
  // CFTSign arrivait donc sur l'ecran d'accueil d'un utilisateur de Flexcore, et le
  // manifeste installait la PWA du poseur avec les icones de l'autre marque.
  //
  // ⚠️ CE QUI RESTE FAUX AVANT L'EXECUTION DU PAQUET. Entre le premier octet servi et
  // cette ligne, le navigateur ne connait aucune icone et va chercher `/favicon.ico`,
  // qui n'existe pas. C'est le meme arbitrage que le titre neutre de `index.html` :
  // aucune icone pendant un battement vaut mieux que l'icone de l'autre marque, qui,
  // elle, reste. Le jour ou une image neutre sera dessinee, elle se posera en dur ici
  // et ce battement disparaitra.
  poserLien('icon', identite.logo, { type: 'image/png', sizes: '192x192' });
  poserLien('apple-touch-icon', identite.iconeApple);
  poserLien('manifest', identite.manifeste);

  // Les six variables de couleur, en deux familles.
  //
  // Le hexadecimal brut sert les rares styles EN LIGNE qu'aucun utilitaire Tailwind ne
  // couvre -- la bulle de la carte est construite comme une chaine HTML. Les canaux
  // servent le theme Tailwind (`bg-marque`, `text-marque`, `border-marque-bordure`),
  // seule forme qui accepte les modificateurs d'opacite.
  //
  // ⚠️ LES SIX, OU AUCUNE. En poser une partie laisserait les autres au repli de la
  // feuille de style. Le defaut est invisible tant que les deux marques portent la meme
  // valeur -- c'est le cas depuis le 07/09/2026 -- et il reapparaitra tel quel le jour
  // ou l'une des deux divergera : un ecran a deux teintes, sans erreur nulle part.
  const racine = document.documentElement;
  const tons = {
    '--marque-accent': identite.couleur,
    '--marque-accent-clair': identite.couleurClaire,
    '--marque-bordure': identite.couleurBordure,
  };
  Object.entries(tons).forEach(([nom, valeur]) => {
    racine.style.setProperty(nom, valeur);
    racine.style.setProperty(nom + '-rvb', enCanaux(valeur));
  });

  // ET LE JETON `--primary` DE LA BIBLIOTHEQUE DE COMPOSANTS.
  //
  // C'est la seconde source de bleu nuit, et elle ne ressemble pas a la premiere : elle
  // n'est ecrite dans aucun ecran, mais dans `index.css`, en TSL -- `240 28% 14%`, qui
  // vaut exactement #1a1a2e. Le bouton par defaut de `shared/ui/button.jsx` la consomme
  // en `bg-primary`, donc TOUS les boutons « Suivant » de l'assistant restaient bleu
  // nuit sur cftsign.fr. Aucune garde sur le source du parcours ne pouvait le voir : le
  // hexadecimal n'y est pas. C'est la recette navigateur qui l'a trouve, le 31/08, en
  // balayant les couleurs REELLEMENT PEINTES.
  //
  // Sur Flexcore la valeur recalculee est celle qui etait ecrite, au chiffre pres : le
  // CRM ne bouge pas d'un pixel. Sur CFTSign le bouton prend enfin la couleur de sa
  // marque.
  racine.style.setProperty('--primary', enCanauxTsl(identite.couleur));
  // Lisible depuis une feuille de style, pour ce qui se regle mieux en CSS qu'en JS.
  racine.setAttribute('data-marque', MARQUE);
}

/**
 * Les chemins que CFTSign sert. Tout le reste y renvoie vers l'ecran installateur.
 *
 * Les trois premiers sont le parcours terrain lui-meme. Les suivants sont PUBLICS et
 * doivent survivre : sans `/login` le poseur ne peut pas entrer, sans `/onboarding` et
 * `/enregistrement-produit` le client ne peut pas activer son materiel, sans
 * `/signature-mandat` il ne peut pas signer le mandat qu'on lui a envoye — et le renvoi
 * serait SILENCIEUX, donc un client qui ne signe jamais sans que rien ne le dise — et
 * sans les parcours de mot de passe une invitation par courriel arrive sur une porte
 * fermee.
 */
export const CHEMINS_CFTSIGN = [
  '/installateur',
  '/onboarding',
  '/enregistrement-produit',
  '/signature-mandat',
  '/login',
  '/forgot-password',
  '/reset-password',
  '/set-password',
  '/changer-mot-de-passe',
];

export function cheminAutoriseSurCftsign(chemin) {
  return CHEMINS_CFTSIGN.some((c) => chemin === c || chemin.startsWith(c + '/'));
}
